import { sortBasedOnReferenceOrder } from "./sortBasedOnReferenceOrder";

/**
 * Sorts an array based on a reference order, then swaps two elements at specified indices.
 * This function first sorts the dataset according to the reference order (elements not in
 * reference appear at the end), then performs a swap operation on the sorted result.
 *
 * The function creates a copy of the input array, so the original dataset is not modified.
 * If either swap index is out of bounds, the function returns the sorted array without
 * performing the swap operation.
 *
 * @template T The type of elements in the arrays
 * @param {T[]} dataSet - The array to be sorted and swapped (original array is preserved)
 * @param {T[]} referenceOrder - The reference array that defines the desired sort order
 * @param {number} swapIndexA - The first index to swap in the sorted array
 * @param {number} swapIndexB - The second index to swap in the sorted array
 * @returns {T[]} A new array that is sorted by reference order with the specified elements swapped
 *
 * @see {@link sortBasedOnReferenceOrder} for details on the sorting behavior
 *
 * @example
 * // Basic sort and swap
 * const data = ['c', 'a', 'b'];
 * const order = ['a', 'b', 'c'];
 * const result = sortAndSwap(data, order, 0, 2);
 * // First sorts to ['a', 'b', 'c'], then swaps indices 0 and 2
 * // Result: ['c', 'b', 'a']
 * // Original data remains: ['c', 'a', 'b']
 *
 * @example
 * // With elements not in reference order
 * const data = ['z', 'b', 'y', 'a'];
 * const order = ['a', 'b'];
 * const result = sortAndSwap(data, order, 0, 3);
 * // First sorts to ['a', 'b', 'z', 'y'], then swaps indices 0 and 3
 * // Result: ['y', 'b', 'z', 'a']
 *
 * @example
 * // Invalid indices - returns sorted array unchanged
 * const data = ['c', 'a', 'b'];
 * const order = ['a', 'b', 'c'];
 * const result = sortAndSwap(data, order, 0, 10);
 * // Index 10 is out of bounds, so returns sorted array: ['a', 'b', 'c']
 */
export function sortAndSwap<T>(
  dataSet: T[],
  referenceOrder: T[],
  swapIndexA: number,
  swapIndexB: number
) {
  const orderedIds = sortBasedOnReferenceOrder([...dataSet], referenceOrder);
  // validate indices
  if (
    orderedIds[swapIndexA] === undefined ||
    orderedIds[swapIndexB] === undefined
  ) {
    return orderedIds;
  }
  [orderedIds[swapIndexA], orderedIds[swapIndexB]] = [
    orderedIds[swapIndexB] as T,
    orderedIds[swapIndexA] as T,
  ];
  return orderedIds;
}
